import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { constants, DefaultLanguageCode, language as languageModel, ListLanguageCode, paths, relativePaths, RibbonType } from '@app/constants';
import { State } from '@app/core/features';
import { AuthService, FetchLoggedInUser, selectors as authSelectors, UserData } from '@app/core/features/auth';
import { MyChartsService } from '@app/core/features/charts-manager/my-charts/my-charts.service';
import { ChartsStoreActions, SelectedLanguageModel } from '@app/core/features/charts-store';
import { ChartsStoreService } from '@app/core/features/charts-store/charts-store.service';
import { PaymentService } from '@app/core/features/payment/payment.service';
import { LanguageService } from '@app/core/services/language/language.service';
import { TouchService } from '@app/core/services/touch/touch.service';
import { OverviewComponent } from '@app/pages/overview/overview.component';
import { Globals } from '@app/shared/models';
import { getRandomNumber, isMobileView } from '@app/utils';
import { MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { environment } from '@env/index';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { BaseComponent } from '../base/base/base.component';

@Component({
  providers: [OverviewComponent],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() loggedInUser: UserData;
  @Input() hasError: boolean;
  options = languageModel;
  selectedItem = this.options[0];
  relativePaths = relativePaths;
  config = environment;
  isOpenMenuHeader: boolean;
  isLanguagePopoverShow: boolean;
  data = new BehaviorSubject<UserData>({} as UserData);
  isChartManager: boolean;
  isHidemycharts = false;
  isLoadCart = true;
  isSigninPage: boolean;
  isChartStore: boolean;
  isSupport: boolean;
  isMobileView: boolean;
  isOverview: boolean;
  isHowToGuide: boolean;
  ribbonClass: string;
  ribbonText: string;
  count$: Observable<number>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private chartStoreService: ChartsStoreService,
    // this service is in-use by template, please do not remove
    public touchService: TouchService,
    public globals: Globals,
    protected store: Store<State>,
    private languageService: LanguageService,
    private paymentService: PaymentService,
    private myChartsService: MyChartsService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    super();
    this.isLanguagePopoverShow = false;
    this.count$ = this.paymentService.count$;
    router.events.subscribe(event => {
      this.isOpenMenuHeader = isMobileView() ? false : true;
      this.isChartStore = this.router.url.substr(1).startsWith(paths.LIGHT_HOUSE_CHART);
      this.isChartManager = this.router.url.substr(1).startsWith(paths.CHARTS_MANAGER) 
      || this.router.url.substr(1).startsWith(paths.DOWNLOAD_RAYCONNECT);
      this.isOverview = this.router.url.substr(1).startsWith(paths.OVERVIEW);
      this.isHowToGuide = this.router.url.substr(1).startsWith(paths.HOW_TO_GUIDE);
      this.isSupport = this.router.url.substr(1).startsWith(paths.SUPPORT);
      this.isMobileView = isMobileView();

      if (event instanceof NavigationEnd && isMobileView()) {
        this.checkIsHideMyCharts();
      }
    });
  }

  ngOnInit() {
    // SSO Login
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        console.log('Login Success');
        const payload = result.payload as AuthenticationResult;
        this.authService.setActiveAccount(payload.account);
        this.authService.authentication_result = payload;

        this.store.dispatch(new FetchLoggedInUser());

        this.store.pipe(authSelectors.getHasError).subscribe(result => this.hasError = result);
      });

    this.store.pipe(authSelectors.getUserData).subscribe(result => {
      this.loggedInUser = result
      if (this.checkLoginSuccess()) {
        if (this.loggedInUser.language) {
          this.findAndTranslate(this.loggedInUser.language);
        }
        this.commonSetting();
      }
    });

    this.setHeaderRibbon();
    this.initVariables();
    this.isChartStore = this.router.url.substr(1).startsWith(paths.LIGHT_HOUSE_CHART);
    this.isChartManager = this.router.url.substr(1).startsWith(paths.CHARTS_MANAGER);
    this.isOverview = this.router.url.substr(1).startsWith(paths.OVERVIEW);
    this.isHowToGuide = this.router.url.substr(1).startsWith(paths.HOW_TO_GUIDE);
    this.isSigninPage = this.router.url.substr(1).startsWith(paths.SIGN_IN);
    this.isMobileView = isMobileView();

    if (this.checkLoginSuccess() && !this.loggedInUser.language){
      if (this.cookieService.get('lang')) {
        this.updateCurrentUserLangue(languageModel.find(row => row.code === this.cookieService.get('lang')));
      } else {
        this.updateCurrentUserLangue(languageModel.find(row => row.code === this.loggedInUser.countryCode));
      }
    } else if (!this.checkLoginSuccess()){
      if (this.cookieService.get('lang')) {
        this.findAndTranslate(this.cookieService.get('lang'))
      } else {
        // Set default language is EU at the first time load the page
        this.findAndTranslate(DefaultLanguageCode);
        if (window.navigator && window.navigator.geolocation) {
          window.navigator.geolocation.getCurrentPosition(
            position => {
              this.languageService.getCountryCodeByCoordinate(position.coords.latitude, position.coords.longitude)
                .subscribe(res => {
                  if (res && res !== DefaultLanguageCode) {
                    this.findAndTranslate(res);
                  }
                });
            }
          );
        }
      }
    }

    this.route.queryParams.subscribe(params => {
      if (params && params.lang && ListLanguageCode.includes(params.lang)) {
        this.updateCurrentUserLangue(languageModel.find(row => row.code === params.lang));
      }
    });

    this.changesEvent();
  }

  private checkLoginSuccess = (): boolean => this.authService.checkoutAccount() && this.loggedInUser && !this.hasError;

  private commonSetting = (): void => {
    this.setCurrentUserInfo();
    this.data.next(this.loggedInUser);
  }

  private findAndTranslate = (lang: string) => {
    this.translateLanguage(languageModel.find(row => row.code === lang));
  }

  private updateCurrentUserLangue = (selectedLanguage: SelectedLanguageModel) => {
    if (this.checkLoginSuccess()) {
      this.chartStoreService.saveLanguage({ language: selectedLanguage.code }).subscribe(() => {
        this.myChartsService.checkChangedLanguage.next(getRandomNumber());
      });
    }
    this.translateLanguage(selectedLanguage);
   
  }

  setCurrentUserInfo = (): void => {
    const { loggedInUser: {
        id,
        email,
        firstName,
        lastName,
        fullName,
        loggedInUserId,
        isFirstTimeDraw,
        isFirstTimeReachBoxLimit,
        city,
        state,
        country,
        countryCode,
        accountType,
        roles,
        postalCode,
        language,
        zuoraAccountId,
        accountId,
        zuoraPaymentGateway,
        iso2,
        currency,
        unit,
        mfdType,
        streetAddress,
        streetAddress2,
        phoneNumber
      }
    } = this;

    this.authService.updateUserData({
      id,
      city,
      state,
      country,
      postalCode,
      language,
      zuoraAccountId,
      accountId,
      zuoraPaymentGateway,
      iso2,
      currency,
      unit,
      email,
      firstName,
      lastName,
      fullName,
      loggedInUserId,
      isFirstTimeDraw,
      isFirstTimeReachBoxLimit,
      countryCode,
      accountType,
      roles,
      mfdType,
      streetAddress,
      streetAddress2,
      phoneNumber
    });

    /**
     * In case of the mix action between login and anonymous user,
     * the shopping cart of login user will merge all item from the anonymous user (if exist any)
     */
    this.isLoadCart = true;
    this.chartStoreService.setUserId(loggedInUserId);
    this.paymentService.setZuoraUserId(loggedInUserId);
    this.paymentService.countItemInCart();
    this.initLanguageValue(countryCode);
    setTimeout(() => {
      this.chartStoreService.checkEditListItemOnCart.next(getRandomNumber());
    }, 0);
  }

  private changesEvent() {
    this.languageService.checkChangeLanguage
      .pipe(distinctUntilChanged())
      .subscribe(() => this.initLanguageValue(this.cookieService.get('lang')));

    /* this.chartStoreService.checkEditListItemOnCart
      .pipe(distinctUntilChanged())
      .subscribe(
        () => {
          this.authService.routingAfterSSOInteract(paths.CHARTS_MANAGER);
        },
        () => {
          this.isLoadCart = false;
          this.authService.routingAfterSSOInteract(paths.CHARTS_MANAGER);
        },
        () => {
          this.isOpenMenuHeader = isMobileView() ? false : true;
          this.authService.routingAfterSSOInteract(paths.CHARTS_MANAGER);
        }
      ); */

    this.chartStoreService.hidePopoverMenuSubject
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.isOpenMenuHeader = isMobileView() ? false : true;
      });
  }

  private initVariables() {
    this.paymentService.countItemInCart();

    if (isMobileView()) {
      this.isOpenMenuHeader = false;
      this.checkIsHideMyCharts();
    } else {
      this.isOpenMenuHeader = true;
    }
  }

  checkIsHideMyCharts() {
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams.hidemycharts === undefined) {
        this.isHidemycharts = this.chartStoreService.getHidemycharts();
      } else {
        this.chartStoreService.setHidemycharts(queryParams.hidemycharts);
        this.isHidemycharts = queryParams.hidemycharts === 'true';
      }
    });
  }

  openMenuHeader(event) {
    event.preventDefault();
    this.isOpenMenuHeader = !this.isOpenMenuHeader;
  }

  onLogout(event) {
    event.preventDefault();
    this.globals.isDisplayProgressSpinner = true;
    this.authService.logOut();
  }

  onLogin(event) {
    event.preventDefault();

    if (!this.isSigninPage) {
      this.cookieService.set(constants.returnUrl, this.router.url, null, '/');
    }

    this.authService.loginRedirect();
  }

  onCreateAccount(event) {
    event.preventDefault();
    this.authService.loginRedirect();
  }

  onEditProfile(event) {
    event.preventDefault();

    this.cookieService.set(constants.returnUrl, this.router.url, null, '/');
    this.authService.editProfile();
  }

  onMyAccount(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    this.router.navigate([paths.MY_ACCOUNT], { replaceUrl: false });
  }

  setLanguage(event, selectedLanguage: SelectedLanguageModel) {
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
    }
    if (selectedLanguage.code === this.selectedItem.code) {
      return;
    }
    this.updateCurrentUserLangue(selectedLanguage);
  }

  translateLanguage(selectedLanguage: SelectedLanguageModel) {
    this.isLanguagePopoverShow = false;
    this.selectedItem = selectedLanguage;
    this.cookieService.set('lang', selectedLanguage.code, 365, '/');
    this.translateService.use(selectedLanguage.code);
    this.getJsonData(selectedLanguage.code);
  }

  private getJsonData(lang: string) {
    this.store.dispatch(new ChartsStoreActions({ lang }));
    this.chartStoreService.checkChangeLanguage.next(getRandomNumber());
    this.languageService.findMetaData();
  }

  toggleLanguagePopoverOn() {
    this.isLanguagePopoverShow = true;
  }

  initLanguageValue(currentLang: string) {
    currentLang = this.cookieService.get('lang') ? this.cookieService.get('lang') : currentLang;
    this.selectedItem = this.options.find(element => element.code === currentLang);
  }

  openCart(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    this.router.navigate([paths.PAYMENT], { replaceUrl: false });
  }

  isGroup(index) {
    return index === 0 ? true : this.options[index - 1].group === this.options[index].group;
  }

  myChartClick(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
      this.router.navigate([paths.DOWNLOAD_RAYCONNECT], { replaceUrl: false });
    } else {
      this.router.navigate([paths.CHARTS_MANAGER], { replaceUrl: false });
    }
  }

  supportClick(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
    }

    this.router.navigate([paths.SUPPORT], { replaceUrl: false });
  }

  chartStoreClick(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
    }

    this.router.navigate([paths.LIGHT_HOUSE_CHART], { replaceUrl: false });
  }

  public overviewClick(event) {
    this.closeSidenavMenu();
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
    }

    this.router.navigate([paths.OVERVIEW], { replaceUrl: false });
  }

  public howToGuideClick(event) {
    event.preventDefault();
    if (isMobileView()) {
      this.isOpenMenuHeader = false;
    }
    this.router.navigate([paths.HOW_TO_GUIDE], { replaceUrl: false });
  }

  private setHeaderRibbon() {
    this.ribbonClass = 'ribbon top-left ';
    this.ribbonText = RibbonType[this.config.ribbonType];
    switch (this.config.ribbonType) {
      case RibbonType.Local:
        this.ribbonClass += 'ribbon-success';
        break;
      case RibbonType.Dev:
        this.ribbonClass += 'ribbon-danger';
        break;
      case RibbonType.Stage:
        this.ribbonClass += 'ribbon-info';
        break;
      case RibbonType.Preprod:
        this.ribbonClass += 'ribbon-warning';
        break;

      default:
        this.ribbonClass = 'ribbon hidden';
        this.ribbonText = '';
        break;
    }
  }

  public openSidenavMenu(): void {
    const item = document.getElementById('sidenav-menu') as HTMLElement;
    let body = document.getElementsByTagName('body')[0];
    let approot = document.getElementsByTagName('app-root')[0];
    item.classList.add('show');
    body.classList.add('menu-is-open');
    approot.classList.add('menu-footer');
  }

  public closeSidenavMenu() {
    const item = document.getElementById('sidenav-menu') as HTMLElement;
    let approot = document.getElementsByTagName('app-root')[0];
    let body = document.getElementsByTagName('body')[0];
    item.classList.remove('show');
    body.classList.remove('menu-is-open');
    approot.classList.remove('menu-footer');
  }
}
